import { getScopedTranslate } from '@locale/translate';

import type { FieldMappingFieldItem, FieldMappingGroup } from './field-mapping';

const locale = {
  en: {
    traveller: 'Traveller',
    company: 'Company',
    journeyDetails: 'Journey details',
    journeyData: 'Journey data',
    action: 'Action',
  },
};

export const labelSeparator = ' • ';

export const isTravellerVariableSource = (variableSource: FieldMappingFieldItem['source'] = '') => {
  return (
    variableSource === 'company_traveller' ||
    variableSource === 'traveller' ||
    variableSource === 'traveller_journey' ||
    variableSource.startsWith('company_traveller_attribute') ||
    variableSource.startsWith('journey_attribute')
  );
};

// A variable whose source/action was deleted or severed carries a `_MISSING`
// suffix (mirrors the backend MISSING_SOURCE_ACTION_POSTFIX).
export const MISSING_SOURCE_SUFFIX = '_MISSING';

// The single frontend definition of "broken" -- editor pill, header chips and
// preview helpers all call this; don't add a second. It relies on the persisted
// `_MISSING` marker, whereas the backend decides by graph reachability, so the
// two can disagree (the marker can be missing on a truly broken var). Durable
// fix: a server-stamped `isBroken` so every surface agrees.
export const isVariableSourceMissing = ({
  source = '',
  action = '',
}: {
  source?: string;
  action?: string;
}): boolean =>
  (source === 'action' && action.endsWith(MISSING_SOURCE_SUFFIX)) ||
  (source.startsWith('journey_attribute') && source.endsWith(MISSING_SOURCE_SUFFIX)) ||
  (source.startsWith('company_traveller_attribute') && source.endsWith(MISSING_SOURCE_SUFFIX));

const getVariableSource = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
): string => {
  const { t } = getScopedTranslate(locale);
  if (isTravellerVariableSource(variable.source)) {
    return t('traveller');
  }
  if (variable.source === 'company') {
    return t('company');
  }
  if (variable.source === 'journey') {
    return t('journeyDetails');
  }
  if (variable.source === 'journey_data') {
    return t('journeyData');
  }
  if (variable.source === 'action') {
    const action = variableGroups?.find((group) => group.id === variable.action);
    return action?.label ?? t('action');
  }
  return variable.source;
};

export const getVariableLabel = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
) => {
  // Gate on the broken marker, not a failed group lookup: the previews call this
  // without variableGroups (every lookup fails there, but healthy vars still need
  // a prefix). A broken var's label is already a qualified "Source • Field", so
  // return as-is -- prepending "Action" would give "Action • Email • Subject".
  if (isVariableSourceMissing(variable)) {
    return variable.label;
  }

  const prettySource = getVariableSource(variable, variableGroups);
  const variableLabel =
    variableGroups
      ?.map(
        (group) =>
          group.fields.find((field) => {
            return (
              variable.field === field.field &&
              (variable.action === 'undefined' || variable.action === undefined
                ? field.source === variable.source
                : variable.action === field.action)
            );
          })?.label
      )
      .find((label) => label !== undefined) ?? variable.label;

  const prefix = `${prettySource}${labelSeparator}`;
  if (variableLabel.startsWith(prefix)) {
    return variableLabel;
  }
  return `${prefix}${variableLabel}`;
};
